/* eslint-disable max-lines */

import React, { useEffect, useState } from 'react'
import { NavLink, useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'
import ArrowIcon from '@mui/icons-material/TrendingFlat'
import InfoIcon from '@mui/icons-material/Info'

import { Button, type ButtonStyles } from '@nexiahome/ui-common/components/primitives/buttons'
import PaywallAccountFields from 'pages/paywall/PaywallAccountFields'
import Spinner from 'components/Spinner'
import StatesAndProvinces from 'components/StatesDropdown/StatesAndProvinces'

import Brand from 'helpers/brand'
import Environment from 'helpers/environment'
import GA4 from 'helpers/ga4'
import { isEmailValid, isUSZipValid, isCanadaPostalValid } from 'helpers/validations'
import WindowLocation from 'helpers/windowLocation'

import useDealerContactInfo from 'hooks/useDealerContactInfo'
import { useFeaturesContext } from 'hooks/FeaturesContext'
import useActivePremiumAccount from 'hooks/useActivePremiumAccount'
import usePaywallPremiumAcct from 'hooks/usePaywallPremiumAcct'

import Api from 'services/api'

import { color } from 'styles/colors'
import { StackedButtonContainer, SinglePanePage, PageTitle, screenSize } from 'styles/shared'

import { BillingData } from 'types/BillingData'

const PaywallAccount = () => {
  const navigate = useNavigate()

  const { dealerId, planNo, planType } = useParams()

  const { activeAccount, isActiveAccountLoaded } = useActivePremiumAccount(dealerId)
  const { premiumAccount, isPremiumAcctLoaded } = usePaywallPremiumAcct(dealerId)
  const { dealerContactInfo, isDealerContactInfoLoaded } = useDealerContactInfo()
  const { enabledFeatures } = useFeaturesContext()

  const [paywallAcctAttributes, setPaywallAcctAttributes] = useState<BillingData>()
  const [isNewAccount, setIsNewAccount] = useState<boolean>(true)
  const [finalAmount, setFinalAmount] = useState<string>('')
  const [finalAmountNote, setFinalAmountNote] = useState<string>('')
  const [total, setTotal] = useState<number>(-1)
  const [errors, setErrors] = useState({ firstName: '', lastName: '', address: '', city: '', state: '', zip: '', email: '' })

  const verifyTaxInfo = (acctAttributes: BillingData) => {
    let zipError = ''
    const countryCode = findCountry(acctAttributes?.state)

    if (!acctAttributes?.zip) zipError = 'Invalid'
    if (countryCode === 'CA' && acctAttributes?.zip && !isCanadaPostalValid(acctAttributes.zip)) zipError = 'Invalid'
    if (countryCode === 'US' && acctAttributes?.zip && !isUSZipValid(acctAttributes.zip)) zipError = 'Invalid'

    if (zipError === '') {
      Api.Paywall.calculateTotal(Number(dealerId), Number(planNo), { ...acctAttributes, ...{ firstName: 'xxx', lastName: 'xxxx' } })
        .then(res => {
          setTotal(Number(res.total))
        }).catch(() => {
          setTotal(-1)
        })
    } else {
      setTotal(-1)
    }
  }

  useEffect(() => {
    if (!isActiveAccountLoaded || !isPremiumAcctLoaded) return

    if (premiumAccount && !activeAccount) {
      setIsNewAccount(false)
    }
  }, [isActiveAccountLoaded, isPremiumAcctLoaded, activeAccount, premiumAccount])

  useEffect(() => {
    if (isActiveAccountLoaded && activeAccount) {
      setPaywallAcctAttributes(activeAccount.billingContact)
      verifyTaxInfo(activeAccount.billingContact)
    } else if (isDealerContactInfoLoaded && dealerContactInfo) {
      const countryCode = findCountry(dealerContactInfo.state)
      const attributes = {
        address2: '',
        firstName: '',
        lastName: '',
        address: dealerContactInfo.address || '',
        city: dealerContactInfo.city || '',
        state: dealerContactInfo.state || '',
        zip: dealerContactInfo.zip || '',
        email: dealerContactInfo.email || '',
        country: countryCode || ''
      }

      setPaywallAcctAttributes(attributes)
      verifyTaxInfo(attributes)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActiveAccountLoaded, activeAccount, dealerContactInfo, isDealerContactInfoLoaded])

  useEffect(() => {
    if (total === -1) {
      setFinalAmountNote(planType === 'diag_monthly' ? 'USD $250.00 will be billed to your account monthly. *Tax not calculated' : 'USD $2,400.00 will be billed to your account annually. *Tax not calculated')

      setFinalAmount(planType === 'diag_monthly' ? '$250.00' : '$2,400.00')
    } else {
      const formatterUSD = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })

      setFinalAmountNote(planType === 'diag_monthly' ? `USD ${formatterUSD.format(total)} will be billed to your account monthly.` : `USD ${formatterUSD.format(total)} will be billed to your account annually.`)
      setFinalAmount(formatterUSD.format(total))
    }
  }, [total, planType])

  const findCountry = (stateCode: string = '') => {
    let countryCode = 'US'

    for (let i = 0; i < StatesAndProvinces.CAN_PROVINCES.length; i++) {
      if (StatesAndProvinces.CAN_PROVINCES[i].value === stateCode) {
        countryCode = 'CA'
        break
      }
    }

    return countryCode
  }

  const onChangeCallback = (updatedAttribute) => {
    if (updatedAttribute.firstName) errors.firstName = ''
    if (updatedAttribute.lastName) errors.lastName = ''
    if (updatedAttribute.address) errors.address = ''
    if (updatedAttribute.city) errors.city = ''
    if (updatedAttribute.state) errors.state = ''
    if (updatedAttribute.zip) errors.zip = ''
    if (updatedAttribute.email) errors.email = ''

    setErrors({ ...errors })

    if (updatedAttribute.state) updatedAttribute.country = findCountry(updatedAttribute.state)
    verifyTaxInfo({ ...paywallAcctAttributes, ...updatedAttribute })
    setPaywallAcctAttributes({ ...paywallAcctAttributes, ...updatedAttribute })
  }

  const validateFields = () => {
    const newErrors = { firstName: '', lastName: '', address: '', city: '', state: '', zip: '', email: '' }
    const countryCode = findCountry(paywallAcctAttributes?.state) || 'US'

    if (!paywallAcctAttributes?.firstName) newErrors.firstName = 'First name is required'
    if (!paywallAcctAttributes?.lastName) newErrors.lastName = 'Last name is required'
    if (!paywallAcctAttributes?.address) newErrors.address = 'Address is required'
    if (!paywallAcctAttributes?.city) newErrors.city = 'City is required'
    if (!paywallAcctAttributes?.state) newErrors.state = 'State is required'
    if (!paywallAcctAttributes?.zip) newErrors.zip = 'Zip code is required'
    if (!paywallAcctAttributes?.email) newErrors.email = 'Email is required'
    if (paywallAcctAttributes?.email && !isEmailValid(paywallAcctAttributes.email)) newErrors.email = 'Invalid email'
    if (countryCode === 'CA' && paywallAcctAttributes?.zip && !isCanadaPostalValid(paywallAcctAttributes.zip)) newErrors.zip = 'Invalid postal code'
    if (countryCode === 'US' && paywallAcctAttributes?.zip && !isUSZipValid(paywallAcctAttributes.zip)) newErrors.zip = 'Invalid zip code'
    if (paywallAcctAttributes) verifyTaxInfo(paywallAcctAttributes)

    return newErrors
  }

  const onClickEnterCC = (testAcct: boolean) => {
    const newErrors = validateFields()

    if (newErrors.firstName || newErrors.lastName || newErrors.address || newErrors.city || newErrors.state || newErrors.zip || newErrors.email) {
      setErrors(newErrors)
    } else if (paywallAcctAttributes) {
      setErrors(newErrors)
      const text = `Click CC - planNo: ${planNo}`
      GA4.sendEvent({ category: 'Dealer Paywall', label: 'Dealer Paywall Add CC', action: `${text}` })

      if (testAcct) {
        Api.Paywall.setupTestAccount(Number(dealerId), Number(planNo), paywallAcctAttributes)
          .then(() => {
            navigate('/dashboard/paywall/accept')
          }).catch(() => {
            navigate('/dashboard/paywall/failure')
          })
      } else {
        Api.Paywall.triggerCybersource(Number(dealerId), Number(planNo), paywallAcctAttributes)
          .then(res => {
            WindowLocation.assign(res.postUrl + '/?' + (new URLSearchParams(res.cybersourceFields).toString()))
          }).catch()
      }
    }
  }

  if (!isActiveAccountLoaded || !isDealerContactInfoLoaded) {
    return <S.EmptyLoadingPage><Spinner variant="page" /></S.EmptyLoadingPage>
  }

  return (
    <S.Container>
      <S.Top>
        <Button decoration="link" onPress={() => navigate(-1)} aria-label="back-button">
          <S.ArrowIcon fontSize="large" />
        </Button>

        <S.ProductLogo src={Brand.premiumProductLogoPath()} alt={`${Brand.brandName()} logo`} />
        <div />
      </S.Top>

      <S.Page>
        <S.PageTitle>Billing Information</S.PageTitle>

        {isNewAccount && enabledFeatures?.includes('paywall_trial') && (
          <div>30 Day free trial, which will automatically roll over to the
            <NavLink to={`/paywall/options/${dealerId}`}> {planType === 'diag_monthly' ? 'monthly' : 'annual'} plan (change)</NavLink>
            .
          </div>
        )}

        <S.Form>
          <S.BillingAddressBox>
            <S.Explanation>
              To update your credit card information, please enter your billing address and select
              <strong>&nbsp;Next&nbsp;</strong>
              to continue to payment details.
            </S.Explanation>
            <PaywallAccountFields {...{ ...paywallAcctAttributes, errors, onChangeCallback }} />
          </S.BillingAddressBox>

          {enabledFeatures?.includes('paywall_trial') && (
            <>
              {isNewAccount && (
                <>
                  <S.FreeTrialNote>
                    <S.InfoIcon fontSize="large" />

                    <S.FreeTrialText>By clicking Next, you agree to a
                      <S.RedText> free trial period </S.RedText>
                      of Premium features for 30 days. You may choose to cancel at any time before the end of the trial period and will not be charged. Please see our
                      <NavLink to="/terms_and_conditions" target="_blank" rel="noreferrer"> Terms and Conditions </NavLink>
                      page for additional information.
                    </S.FreeTrialText>
                  </S.FreeTrialNote>

                  <S.FinalAmountText>
                    At the end of the 30 day trial, you will be charged USD {finalAmount}, plus applicable taxes
                  </S.FinalAmountText>
                </>
              )}

              {!isNewAccount && (
                <S.FinalAmountText>{finalAmountNote}</S.FinalAmountText>
              )}
            </>
          )}

          {!enabledFeatures?.includes('paywall_trial') && (
            <S.FinalAmountText>{finalAmountNote}</S.FinalAmountText>
          )}

          <S.ButtonContainer noBorder>
            <Button decoration="secondary" text="Back" styles={buttonStyle} onPress={() => navigate(-1)} />
            <Button decoration="positive" text="Next" styles={buttonStyle} onPress={() => onClickEnterCC(false)} />
            {!Environment.isProduction() && !Environment.isStaging() && (
              <Button decoration="danger" text="Test Account - NO CC" onPress={() => onClickEnterCC(true)} />
            )}
          </S.ButtonContainer>
        </S.Form>
      </S.Page>
    </S.Container>
  )
}

export default PaywallAccount

const buttonStyle: ButtonStyles = {
  button: { width: 80 },
  text: { textTransform: 'capitalize' }
}

const S = {
  Container: styled.div`
    height: 100%;
    background-color: ${color('background')};
  `,
  ArrowIcon: styled(ArrowIcon)`
    transform: rotate(180deg);
    margin-right: 10px;
    color: ${color('breadCrumbsTextHover')};
  `,
  EmptyLoadingPage: styled(SinglePanePage)`
    height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
  `,
  Page: styled(SinglePanePage)`
    margin-top: 20px;
    padding: 35px;
  `,
  ButtonContainer: styled(StackedButtonContainer)`
    margin-top: 0px;
    padding-top: 0px;

    @media ${screenSize.small} {
      margin-top: 20px;
      margin-bottom: 30px;
    }
  `,
  Explanation: styled.span`
    margin-bottom: 30px;
    text-align: left;
    color: ${color('checkboxText')};
    font-size: 12px;
  `,
  FreeTrialNote: styled.div`
    display: flex;
    align-items: center;
    margin-top: 20px;
    background-color: ${color('titleBackground')};
    border-radius: 10px;
  `,
  FreeTrialText: styled.span`
    padding: 5px;
    color: ${color('secondaryText')};
  `,
  InfoIcon: styled(InfoIcon)`
    margin-left: 10px;
    margin-right: 5px;
    color: ${color('questionMarkIcon')};
  `,
  PageTitle: styled(PageTitle)`
    margin-top: 10px;
    margin-bottom: 5px;
    font-size: 24px;
    text-transform: capitalize;
  `,
  BillingAddressBox: styled.div`
    display: flex;
    flex-direction: column;
    background-color: ${color('titleBackground')};
    padding: 30px 40px;
    border-radius: 10px;

    @media ${screenSize.small} {
      padding: 20px;
    }
  `,
  Form: styled.form`
    margin-top: 20px;
    max-width: 1000px;
  `,
  RedText: styled.span`
    color: ${color('premiumFreeTrialText')};
    font-weight: 600;
  `,
  FinalAmountText: styled.div`
    text-align: end;
    margin-top: 50px;
    color: ${color('secondaryText')};
    font-size: 12px;
  `,
  ProductLogo: styled.img`
    height: 50px;
    @media ${screenSize.small} {
      max-width: 290px;
    }
  `,
  Top: styled.div`
    display: flex;
    align-items: center;
    padding: 20px;
    border-bottom: 1px solid ${color('border')};
  `
}
